import { useEffect, useState, useMemo } from 'react'
import AdminLayout from '../../layouts/AdminLayout'
import {
  Box, Button, Flex, HStack,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  FormControl, FormLabel, Grid, Input, Select, IconButton,
} from "@chakra-ui/react";
import { FaEdit, FaFileCsv, FaFilePdf } from "react-icons/fa";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import adminService from '../../services/adminService';

import ReactPaginate from "react-paginate";
import { exportToPDF } from '../../utils/pdfExporter';
import { exportToCSV } from '../../utils/csvExporter';

const validationSchema = Yup.object().shape({
  countryId: Yup.string().required("Country Code is required"),
  phone_code: Yup.string().required("Phone Code is required"),
  city_name: Yup.string().required("City Name is required"),
  maxMonthlyRide: Yup.string().required("maxMonthlyRide is required"),
  Stat: Yup.string().required("Stat is required"),
});

const ITEMS_PER_PAGE = 10;

const Cities = () => {

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [data, setData] = useState([])
  const [Country, setCountry] = useState([])
  const [city_id, setCityId] = useState(0)

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    countryList();
    cityList();
  }, [])

  const countryList = async () => {
    try {
      const res = await adminService.getCountry();
      if (res.statusCode === 200) {
        setCountry(res.data)
      }
    } catch (error) {
      alert(error.response?.data?.message || "Something went wrong.");
    }
  }

  const cityList = async () => {
    try {
      const res = await adminService.getCity()

      if (res.statusCode === 200) {
        setData(res.data)
      }
    } catch (error) {
      alert(error.response?.data?.message || "Something went wrong.");
    }
  }

  const handleConfig = async (data) => {
    data.city_id = city_id;
    try {
      const res = await adminService.saveCity(data)

      setCityId(0)
      if (res.statusCode === 200) {
        alert(res.message);
        reset();
        cityList();
      }
    } catch (error) {
      alert(error.response?.data?.message || "Something went wrong.");
    }
  }

  const handleEdit = (row) => {

    setValue("countryId", row.countryId);
    setValue("city_name", row.city_name);
    setValue("phone_code", row.phone_code);
    setValue("maxMonthlyRide", row.maxMonthlyRide);
    setValue("Stat", row.Stat);
    setCityId(row.city_id)

  };

  const filteredData = useMemo(() => {
    let result = [...data];
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter((item) =>
      (`${item.country_name}`.toLowerCase().includes(lowerSearch) ||
        item.city_name.toLowerCase().includes(search.toLowerCase()) ||
        String(item.phone_code).includes(search.toLowerCase()) ||
        String(item.city_id).includes(search.toLowerCase()) ||
        String(item.Stat).includes(lowerSearch))
      );
    }
    if (sortField) {
      result.sort((a, b) => {
        let aVal = a[sortField] ?? '';
        let bVal = b[sortField] ?? '';

        return sortOrder === "asc" ? aVal > bVal ? 1 : -1 : aVal < bVal ? 1 : -1;
      });
    }
    return result;
  }, [data, search, sortField, sortOrder]);

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const currentData = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = ({ selected }) => {
    setPage(selected);
  };

  const exportCSV = () => {
    // data, filename
    exportToCSV(filteredData, "city.csv")
  };

  const exportPDF = () => {
    const columns = [
      { label: "id", accessor: "city_id" },
      { label: "Country Name", accessor: "country.country_name" },
      { label: "City Name", accessor: "city_name" },
      { label: "Phone Code", accessor: "phone_code" },
      { label: "MonthlyRide", accessor: "maxMonthlyRide" },
      { label: "Status", accessor: "Stat" },
    ];

    exportToPDF({
      data: filteredData,      // your actual data array
      columns,
      fileName: "city.pdf"
    });
  };

  return (
    <AdminLayout>
      <Box maxW="1000px" mx="auto" px={4}>
        {/* Form Section */}
        <Box
          bg="white"
          p={6}
          rounded="xl"
          boxShadow="md"
          mb={3}
          border="1px solid"
          borderColor="gray.200"
        >
          <form onSubmit={handleSubmit(handleConfig)}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
              gap={6}
            >

              <FormControl>
                <FormLabel fontSize="sm">Country</FormLabel>
                <Select {...register("countryId")} size="sm">
                  <option value={0}>Select</option>
                  {
                    Country.map((ct, index) => (
                      <option key={ct.id} value={ct.id}>{ct.country_name}</option>
                    ))
                  }
                </Select>
                <Box color="red.500" fontSize="xs">{errors.countryId?.message}</Box>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input type="text" {...register("city_name")} size="sm" />
                <Box color="red.500" fontSize="xs">{errors.city_name?.message}</Box>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Phone Code</FormLabel>
                <Input type="text" {...register("phone_code")} size="sm" />
                <Box color="red.500" fontSize="xs">{errors.phone_code?.message}</Box>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">maxMonthlyRide</FormLabel>
                <Input type="text" {...register("maxMonthlyRide")} size="sm" />
                <Box color="red.500" fontSize="xs">{errors.maxMonthlyRide?.message}</Box>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Stat</FormLabel>
                <Select {...register("Stat")} size="sm">
                  <option value={0}>Enable</option>
                  <option value={1}>Disable</option>
                </Select>
                <Box color="red.500" fontSize="xs">{errors.Stat?.message}</Box>
              </FormControl>

            </Grid>

            <Button type="submit" colorScheme="blue" mt={6} size="sm">
              Submit
            </Button>
          </form>
        </Box>

        <Box
          bg="white"
          p={6}
          rounded="xl"
          boxShadow="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Flex justify="space-between" mb={4} flexWrap="wrap" gap={4}>
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              maxW="300px"
              size="sm"
            />
            <HStack>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FaFileCsv />}
                onClick={exportCSV}
              >
                CSV
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                leftIcon={<FaFilePdf />}
                onClick={exportPDF}
              >
                PDF
              </Button>
            </HStack>
          </Flex>
          <TableContainer overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.100">
                <Tr>
                  <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("city_id")}>#</Th>
                  <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("country_name")}>Country</Th>
                  <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("city_name")}>City</Th>
                  <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("phone_code")}>Phone Code</Th>
                  <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("maxMonthlyRide")}>Monthly Ride</Th>
                  <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("cId")}>Stat</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentData.map((row, index) => (
                  <Tr key={row.city_id}>
                    <Td>{row.city_id}</Td>
                    <Td>{row.country.country_name}</Td>
                    <Td>{row.city_name}</Td>
                    <Td>{row.phone_code}</Td>
                    <Td>{row.maxMonthlyRide}</Td>
                    <Td>{row.Stat === 0 ? "Enable" : "Disable"}</Td>
                    <Td>
                      <IconButton
                        icon={<FaEdit />}
                        aria-label="Edit"
                        borderRadius="full"
                        onClick={() => handleEdit(row)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        <Box mt={6}>
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            pageCount={pageCount}
            onPageChange={handlePageChange}
            containerClassName="pagination"
            activeClassName="active"
            pageClassName="page-item"
            previousClassName="page-item"
            nextClassName="page-item"
            breakClassName="page-item"
            pageLinkClassName="page-link"
            previousLinkClassName="page-link"
            nextLinkClassName="page-link"
            breakLinkClassName="page-link"
          />
        </Box>

        <style>{`
                                      .pagination {
                                        display: flex;
                                        justify-content: center;
                                        list-style: none;
                                        padding: 0;
                                        margin-top: 1rem;
                                      }
                                      .page-item {
                                        margin: 0 5px;
                                      }
                                      .page-link {
                                        padding: 8px 12px;
                                        border-radius: 6px;
                                        background-color: #e2e8f0;
                                        cursor: pointer;
                                      }
                                      .page-link:hover {
                                        background-color: #cbd5e0;
                                      }
                                      .active .page-link {
                                        background-color: #3182ce;
                                        color: white;
                                      }
                                    `}</style>

      </Box>
    </AdminLayout>
  )
}

export default Cities
